import React from 'react';
import { motion } from 'framer-motion';
import { FiDownload, FiGithub, FiLinkedin, FiMail, FiArrowDown } from 'react-icons/fi';
import { 
  HeroContainer, 
  HeroContent, 
  HeroText, 
  HeroTitle, 
  HeroSubtitle, 
  HeroDescription,
  HeroButtons,
  PrimaryButton,
  SecondaryButton,
  SocialLinks,
  SocialLink,
  ScrollIndicator
} from './Hero.styles';
import { personalInfo } from '../../utils/constants';
import useLocale from '../../hooks/useLocale';

const Hero = () => {
  const { t } = useLocale();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.45,
        staggerChildren: 0.12
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };



  const handleScrollToNext = () => {
    const aboutSection = document.querySelector('#about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownloadCV = () => {
    // Download the CV placed in the public/ folder. Append to DOM before clicking for compatibility.
    try {
      const link = document.createElement('a');
      // Use absolute URL to avoid SPA routing issues
      const url = `${window.location.origin}/karim-menem-cv.pdf`;
      link.href = url;
      link.setAttribute('download', 'Karim-Menem-CV.pdf');
      link.setAttribute('rel', 'noopener noreferrer');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // Fallback: open in new tab
      window.open('/karim-menem-cv.pdf', '_blank', 'noopener');
    }
  };

  return (
    <HeroContainer id="hero">

      <HeroContent className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <HeroText>
            <motion.div variants={itemVariants}>
              <HeroSubtitle>{t('greet')}</HeroSubtitle>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <HeroTitle>{t('personal.name')}</HeroTitle>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <HeroSubtitle $large>{t('personal.title')}</HeroSubtitle>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <HeroDescription>
                {t('personal.heroDescription')}
              </HeroDescription>
            </motion.div>

            <motion.div variants={itemVariants}>
              <HeroButtons>
                <PrimaryButton
                  as={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('cta.viewWork')}
                </PrimaryButton>
                
                <SecondaryButton
                  as={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDownloadCV}
                >
                  <FiDownload />
                  {t('cta.downloadCV')}
                </SecondaryButton>
              </HeroButtons>
            </motion.div>

            <motion.div variants={itemVariants}>
              <SocialLinks>
                <SocialLink
                  as={motion.a}
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiGithub />
                </SocialLink>
                
                <SocialLink
                  as={motion.a}
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiLinkedin />
                </SocialLink>
                
                <SocialLink
                  as={motion.a}
                  href={`mailto:${personalInfo.email}`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiMail />
                </SocialLink>
              </SocialLinks>
            </motion.div>
          </HeroText>
        </motion.div>

        <ScrollIndicator
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          onClick={handleScrollToNext}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <FiArrowDown />
          </motion.div>
          <span>{t('scrollIndicator')}</span>
        </ScrollIndicator>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero;